import { defineComponent as _defineComponent } from 'vue'
import ActionBubble, { BubbleType } from '../ActionBubble.vue';
import OverviewDeviceCardKpi from './OverviewDeviceCardKpi.vue';
import OverviewDeviceCardKpiList from './OverviewDeviceCardKpiList.vue';
import OverviewDeviceCard from './OverviewDeviceCard.vue';
import OverviewDeviceCardStateKpi from './OverviewDeviceCardStateKpi.vue';
import { Device } from '@/models/device';
import { LaserTubeDeviceStateData } from '@/models/deviceStateData';
import {
  computed,
  getCurrentInstance,
  onBeforeUnmount,
  onMounted,
  ref,
} from 'vue';
import {
  CutCurrentWorkloadDataPlanInfo,
  CutDeviceCurrentWorkloadData,
  MessageType,
} from '@/models/Charts/chartsData';
import { WorkCenterConsoleOverviewLaserWorkloadDataRetriever } from '@/models/Charts/workCenterConsoleOverviewLaserWorkloadDataRetriever';
import { currentCustomer, currentUser } from '@/utils/permissionUtils';
import { modalService } from '@/library-services/modal.service';
import CutCellControlActiveMessagesModal from '../cut/CutCellControlActiveMessagesModal.vue';
import { BetaFeature } from '@/models/enums/BetaFeature';
import { isEmpty, isNil } from '@/utils/misc';
import { getStateCss } from '@/utils/color';
import { abbreviateNumberWithUnit, formatPercentage } from '@/utils/number';
import { useInterval } from '@/composables/misc';
import { useI18n } from '@/composables/i18n';


export default /*#__PURE__*/_defineComponent({
  __name: 'OverviewLaserTubeDeviceCard',
  props: {
    device: null,
    deviceState: null
  },
  emits: ['click'],
  setup(__props: any, { emit }) {

const props = __props as {
  device: Device;
  /**
   * Object containing the information about state and metrics of the device.
   * Can be undefined if there is no data for the device.
   */
  deviceState: LaserTubeDeviceStateData | undefined;
};





const workloadData = ref<CutDeviceCurrentWorkloadData | null>(null);
const workloadLoading = ref(false);
const isFirstWorkloadLoad = ref(true);

const { t } = useI18n();

const workloadRetriever =
  new WorkCenterConsoleOverviewLaserWorkloadDataRetriever(
    currentCustomer()!.tenantIdDh,
  );

if (props.device.isCut()) {
  useInterval(getWorkloadData, 30);
}

onMounted(async () => {
  if (
    hasCurrentWorkloadBetaFeature.value &&
    customerHasDataPlatform.value &&
    props.device.isCut()
  ) {
    await getWorkloadData();
    isFirstWorkloadLoad.value = false;
  }
});

async function getWorkloadData() {
  try {
    workloadRetriever.abort();
    workloadLoading.value = true;
    workloadData.value = await workloadRetriever.getDataForDevice(
      props.device.deviceId,
    );
  } finally {
    workloadLoading.value = false;
  }
}

const app = getCurrentInstance();
async function openMessageModal() {
  modalService.compositionApi.open(
    {
      component: CutCellControlActiveMessagesModal,
      props: {
        messages: workloadData.value?.currentMessages,
      },
    },
    app,
  );
}

const hasCuttingPlans = computed(
  () => props.device.isCut() && !isEmpty(workloadData.value?.cuttingPlanInfo),
);

const currentPlan = computed(
  () =>
    workloadData.value?.cuttingPlanInfo.find(
      (plan) => plan.progress < COMPLETED_PROGRESS_VALUE,
    ) ??
    workloadData.value?.cuttingPlanInfo[0] ??
    CUTTING_PLAN_INFO_NULL_OBJECT,
);

const hasCurrentWorkloadBetaFeature = computed(() =>
  currentUser().hasBetaFeature(BetaFeature.WorkCenterConsoleCutCurrentWorkload),
);

const customerHasDataPlatform = computed(
  () => currentCustomer()?.hasDataPlatform ?? false,
);

const showWorkload = computed(
  () =>
    hasCurrentWorkloadBetaFeature.value &&
    customerHasDataPlatform.value &&
    (hasCuttingPlans.value || workloadLoading.value),
);

const bubbleLabel = computed(() => {
  if (isEmpty(workloadData.value?.currentMessages)) {
    return undefined;
  }
  return workloadData.value?.currentMessages.length.toString();
});

const bubbleType = computed<BubbleType | undefined>(() => {
  if (isEmpty(workloadData.value?.currentMessages)) {
    return undefined;
  }
  if (
    workloadData.value?.currentMessages.some(
      (x) => x.messageLevel === MessageType.Error,
    )
  ) {
    return 'is-danger';
  }
  if (
    workloadData.value?.currentMessages.some(
      (x) => x.messageLevel === MessageType.Warning,
    )
  ) {
    return 'is-warning';
  }
  return 'is-info';
});

const navigationButtonIcon = computed(() => {
  if (props.device.isCut()) {
    return 'cutting-color';
  }
  if (props.device.isTube()) {
    return 'tube-color';
  }
  return '';
});

const showMessagesIcon = computed(
  () =>
    hasCurrentWorkloadBetaFeature.value &&
    customerHasDataPlatform.value &&
    props.device.isCut(),
);

const hasState = computed(() => !isNil(props.deviceState?.lastState));

const stateStyle = computed(() => getStateCss(props.deviceState!.lastState!));

onBeforeUnmount(() => {
  workloadRetriever.abort();
});

const COMPLETED_PROGRESS_VALUE = 1;
const CUTTING_PLAN_INFO_NULL_OBJECT: CutCurrentWorkloadDataPlanInfo = {
  name: '',
  totalParts: 1,
  completedParts: 0,
  totalRuns: 1,
  completedRuns: 0,
  progress: 0,
} as CutCurrentWorkloadDataPlanInfo;

return { __sfc: true,props, emit, workloadData, workloadLoading, isFirstWorkloadLoad, t, workloadRetriever, getWorkloadData, app, openMessageModal, hasCuttingPlans, currentPlan, hasCurrentWorkloadBetaFeature, customerHasDataPlatform, showWorkload, bubbleLabel, bubbleType, navigationButtonIcon, showMessagesIcon, hasState, stateStyle, COMPLETED_PROGRESS_VALUE, CUTTING_PLAN_INFO_NULL_OBJECT, ActionBubble, OverviewDeviceCardKpi, OverviewDeviceCardKpiList, OverviewDeviceCard, OverviewDeviceCardStateKpi, isEmpty, isNil, abbreviateNumberWithUnit, formatPercentage }
}

})